import toast from "react-hot-toast";
import axiosSetup from "../utils/axiosSetup";
import { IProduct } from "../models/productData.model";
import { IQuotationData } from "../models/quotationData.model";
import ResponseModel from "../models/response.model";

export const fetchLeadStatuses = async (typeOfResponse = "") => {
  const response = await axiosSetup.get(
    "/leads/statuses?columns=" + typeOfResponse,
  );
  return response.data.data;
};

export const fetchOneLead = async (leadId: string) => {
  const response = await axiosSetup.get(`/leads/${leadId}`);
  return response.data;
};

export const updateOneLeadData = async (card: { _id: string; data: any }) => {
  const response = await axiosSetup.put(
    `/leads/updateLeadData/${card._id}`,
    card.data,
  );
  return response.data;
};

export const fetchAllQuotations = async (
  leadId: string,
  page: number,
  limit: number = 8,
) => {
  try {
    const response = await axiosSetup.get(
      `leads/quotations/${leadId}?page=${page}&limit=${limit}`,
    );

    if (response.status !== 200) {
      throw new Error(`Failed to fetch EPC data: ${response.statusText}`);
    }

    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to fetch EPC data",
    );
  }
};

export const createQuotation = async (
  bookedProducts: IProduct[],
  leadId: string,
  transportPrice: number = 0,
  discountPrice: number = 0,
  customGst?: number | null,
) => {
  if (!bookedProducts || bookedProducts.length === 0) {
    toast.error("No products selected.");
    throw new Error("No products selected.");
  }

  const quotationProducts = [];

  for (const item of bookedProducts) {
    if (!item.price && item.price !== 0) {
      toast.error(`Price is missing for product with ID: ${item._id}`);
      throw new Error(`Missing price for product with ID: ${item._id}`);
    }

    quotationProducts.push({
      _id: item._id,
      type: item.type,
      unit: item.unit,
      quantity: item.quantity,
      price: item.price,
      gst: customGst ? customGst : item.gst,
      hsn: item.hsn,
      discount: item.discount,
    });
  }

  const requestBody = {
    products: quotationProducts,
    transportPrice,
    discountPrice,
    customGst,
  };

  try {
    const response = await axiosSetup.post(
      `/leads/add-quotation/${leadId}`,
      requestBody,
    );

    return response.data.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to create quotation");
    throw error;
  }
};

export const updateQuotationApi = async (
  leadId: string,
  quotationId: string,
  products: IProduct[],
  transportPrice: number,
  discountPrice: number,
  oldQuotation?: IQuotationData,
) => {
  const payload = {
    products: products.map((p) => ({
      product: p._id,
      quantity: p.quantity,
      price: p.price,
      gst: p.gst,
      hsn: p.hsn,
      unit: p.unit,
      type: p.type,
      discount: p.discount,
    })),
    transportPrice,
    discountPrice,
    oldProducts: oldQuotation?.items,
    oldTransportPrice: oldQuotation?.transportPrice,
    oldDiscountPrice: oldQuotation?.discountPrice,
  };

  try {
    const response = await axiosSetup.put(
      `/leads/update-quotation/${leadId}/${quotationId}`,
      payload,
    );

    return response.data;
  } catch (error: any) {
    toast.error(error.response?.data?.message || "Failed to update quotation");
    throw error;
  }
};

export const getQuotationNumber = async (quotationId: string) => {
  try {
    const response = await axiosSetup.get(
      `/leads/getQuotationNumber/${quotationId}`,
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchAllProducts = async () => {
  try {
    const response = await axiosSetup.get(`product/getAllProduct`);

    const productsArray = response.data.data.products;

    const formattedData = productsArray.reduce(
      (acc: any, product: any) => {
        acc[product.productName] = {
          _id: product._id,
          quantity: product.quantity,
          price: product.price,
          gst: product.gst,
          msp: product.msp,
          hsn: product.hsn,
        };
        return acc;
      },
      {} as Record<string, { _id: string; quantity: number; price: number }>,
    );

    return formattedData;
  } catch (error) {
    throw error;
  }
};
export const handleFetchLeadQuotation = async (leadId: string) => {
  try {
    const response = await axiosSetup.post("/quotation/getLeadQuotation/", {
      leadId: leadId,
    });
    return response.data.data.quotation;
  } catch (error) {
    throw error;
  }
};

export const getQuotationLogs = async (quotationId: string) => {
  try {
    const response = await axiosSetup.get(
      `/quotation/get-quotation-logs/${quotationId}`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error?.response?.data?.message || "Failed to fetch quotation logs",
    );
  }
};

export const getAllItemsApi = async (params: {
  page?: number;
  limit?: number;
  search?: string;
}): Promise<ResponseModel> => {
  try {
    const response = await axiosSetup.get(
      `/quotation/get-all-products-materials`,
      { params },
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const fetchRemarks = async (leadId: string) => {
  const response = await axiosSetup.get(`/leads/getRemark/${leadId}`);
  return response.data;
};

export const getLeadAssignee = async () => {
  const response = await axiosSetup.get("/leads/getLeadAssignee");
  return response.data;
};

export const fetchTotalLeadCount = async () => {
  try {
    const response = await axiosSetup.get("/leads/getTotalLeadCount");
    return response.data.data.totalLeads;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Failed to fetch lead count");
    throw error;
  }
};
